import { Link, useRouterState } from '@tanstack/react-router';
import {
  MessageCircle,
  Users,
  Bell,
  LogOut,
  Share2,
  Building2,
  MessageSquarePlus,
  X,
  type LucideIcon,
} from 'lucide-react';
import { cn } from '@/lib/utils';
import { Avatar, AvatarFallback } from '@/components/ui/avatar';
import { Button } from '@/components/ui/button';
import { useAuth } from '@/hooks/useAuth';

interface NavItem {
  to: string;
  label: string;
  icon: LucideIcon;
}

interface NavSection {
  title?: string;
  items: NavItem[];
}

// Chat Casino: nav podado. Operadores (super-admin) se agrega condicional en el render.
const SECTIONS: NavSection[] = [
  {
    items: [
      { to: '/chats', label: 'Chats', icon: MessageCircle },
      { to: '/clientes', label: 'Jugadores', icon: Users },
      { to: '/notificaciones', label: 'Promos', icon: Bell },
      { to: '/invitacion', label: 'Mi Invitación', icon: Share2 },
      { to: '/bienvenida', label: 'Bienvenida', icon: MessageSquarePlus },
    ],
  },
];

interface SidebarProps {
  open: boolean;
  onClose: () => void;
}

export function Sidebar({ open, onClose }: SidebarProps) {
  const { user, logout } = useAuth();
  const { location } = useRouterState();
  const initials = (user?.display_name || user?.username || '??').slice(0, 2).toUpperCase();

  const sections: NavSection[] = user?.isSuperAdmin
    ? [
        {
          ...SECTIONS[0],
          items: [...SECTIONS[0].items, { to: '/operadores', label: 'Operadores', icon: Building2 }],
        },
        ...SECTIONS.slice(1),
      ]
    : SECTIONS;

  return (
    <>
      {/* Scrim: mobile-only, closes drawer on tap */}
      {open && (
        <div
          className="fixed inset-0 z-30 bg-black/50 lg:hidden"
          onClick={onClose}
          aria-hidden="true"
        />
      )}

      <aside
        className={cn(
          'fixed inset-y-0 left-0 z-40 flex h-screen w-64 shrink-0 flex-col bg-sidebar text-sidebar-foreground transition-transform duration-300 ease-in-out',
          'lg:static lg:translate-x-0',
          open ? 'translate-x-0' : '-translate-x-full',
        )}
      >
        <div className="flex h-16 items-center gap-2 border-b border-sidebar-border px-6">
          <div className="flex h-9 w-9 items-center justify-center rounded-md bg-sidebar-accent text-base font-bold">
            CC
          </div>
          <div className="min-w-0 flex-1">
            <div className="truncate text-sm font-semibold leading-tight">Chat Casino</div>
            <div className="truncate text-xs text-sidebar-foreground/60">Admin Panel</div>
          </div>
          <Button
            variant="ghost"
            size="icon"
            className="h-11 w-11 shrink-0 text-sidebar-foreground/70 hover:bg-sidebar-accent hover:text-sidebar-foreground lg:hidden"
            onClick={onClose}
            aria-label="Cerrar menú"
          >
            <X className="h-5 w-5" />
          </Button>
        </div>

        <nav className="flex-1 overflow-y-auto px-3 py-4">
        {sections.map((section, i) => (
          <div key={i} className={cn(i > 0 && 'mt-6')}>
            {section.title && (
              <div className="px-3 pb-2 text-[10px] font-semibold uppercase tracking-wider text-sidebar-foreground/50">
                {section.title}
              </div>
            )}
            <ul className="space-y-1">
              {section.items.map((item) => {
                const isActive = location.pathname.startsWith(item.to);
                const Icon = item.icon;
                return (
                  <li key={item.to}>
                    <Link
                      to={item.to}
                      onClick={onClose}
                      className={cn(
                        'flex min-h-[44px] items-center gap-3 rounded-md px-3 py-2 text-sm font-medium transition-colors',
                        isActive
                          ? 'bg-sidebar-accent text-sidebar-accent-foreground'
                          : 'text-sidebar-foreground/80 hover:bg-sidebar-accent/50 hover:text-sidebar-foreground',
                      )}
                    >
                      <Icon className="h-4 w-4 shrink-0" />
                      <span className="truncate">{item.label}</span>
                    </Link>
                  </li>
                );
              })}
            </ul>
          </div>
        ))}
      </nav>

      <div className="border-t border-sidebar-border p-3">
        <div className="flex items-center gap-3 rounded-md px-2 py-2">
          <Avatar className="h-9 w-9">
            <AvatarFallback className="bg-sidebar-accent text-sidebar-accent-foreground text-xs">
              {initials}
            </AvatarFallback>
          </Avatar>
          <div className="min-w-0 flex-1">
            <div className="truncate text-sm font-medium">{user?.display_name || user?.username || 'Invitado'}</div>
            <div className="truncate text-xs text-sidebar-foreground/60">{user?.role || '—'}</div>
          </div>
          <Button
            variant="ghost"
            size="icon"
            className="h-11 w-11 text-sidebar-foreground/70 hover:bg-sidebar-accent hover:text-sidebar-foreground"
            onClick={() => void logout()}
            title="Salir"
          >
            <LogOut className="h-4 w-4" />
          </Button>
        </div>
      </div>
      </aside>
    </>
  );
}
